import DateRangeIcon from '@mui/icons-material/DateRange';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import {
    Box,
    Grid2 as Grid,
    Hidden,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import parse, { DOMNode, domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import moment from 'moment';
import React from 'react';
import { normalizeProductHtmlForDisplay } from '../../utils/functions/htmlDescription';
import { Sale } from '../../utils/models/products';

const textPriceSx = {
    priceDisplay: {
        marginBottom: '20px',
        '& .MuiTypography-h5': {
            color: 'white',
            backgroundColor: 'primary.main',
            textAlign: 'center',
            borderStyle: 'solid',
            borderRadius: '20px',
            fontStyle: 'normal',
            width: '170px',
            lineHeight: '2.2rem',
            fontSize: '1.3rem',
        },
    },
    salesSection: {
        maxWidth: { xs: '100%', md: 'calc(80% + 30px)' },
        padding: { xs: '0 20px 10px', sm: 0, md: '0 15px 30px' },
    },
    salesPaper: {
        width: '100%',
        borderRadius: '10px',
        border: { xs: '1px solid rgba(247, 97, 0, 1)', sm: 'none' },
        padding: { xs: '15px 15px 5px', sm: 0 },
    },
    titleSection: {
        backgroundColor: { xs: 'unset', sm: 'secondary.main' },
        display: 'flex',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        alignItems: 'center',
        borderTop: { xs: 'none', sm: '1px solid rgba(255, 233, 201, 1)' },
        borderBottom: { xs: 'none', sm: '1px solid rgba(255, 233, 201, 1)' },
        borderLeft: { xs: 'none', sm: '1px solid rgba(255, 233, 201, 1)' },
        borderBottomLeftRadius: 10,
        borderTopLeftRadius: 10,
        pb: { xs: '5px', sm: 0 },
    },
    textSection: {
        borderTop: { xs: 'none', sm: '1px solid rgba(0, 0, 0, 0.12)' },
        borderRight: { xs: 'none', sm: '1px solid rgba(0, 0, 0, 0.12)' },
        borderBottom: { xs: 'none', sm: '1px solid rgba(0, 0, 0, 0.12)' },
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        padding: { xs: '5px 0 10px', sm: '5px 15px 15px' },
    },
    saleFareSection: {
        padding: { xs: 0, sm: '0 10px' },
        display: 'flex',
        color: 'text.soft',
        flexWrap: 'nowrap',
        maxWidth: '100%',
        width: { xs: '100%', sm: 'auto' },
    },
    descriptionText: {
        textAlign: 'justify',
        color: 'text.soft',
        mb: { xs: '10px', sm: '15px' },
        lineHeight: { xs: 1.35, sm: 1.5 },
    },
    descriptionBold: {
        color: 'text.primary',
        fontWeight: 500,
    },
    list: {
        pt: 0,
    },
    listText: {
        color: 'text.soft',
        fontSize: '1rem',
        pt: '2px',
        pb: '2px',
    },
    boldOverride: {
        mb: '10px',
        '& strong, b': {
            fontWeight: 500,
            marginBottom: '5px',
        },
    },
    boldOverrideP: {
        mt: '30px',
        color: 'text.primary',
        '& strong, b': {
            fontWeight: 500,
        },
    },
} as const;

/** Flatten any node (element or text) to its raw text, for classification only. */
const nodeText = (node: any): string => {
    if (!node) return '';
    if (node.type === 'text') return node.data ?? '';
    if (Array.isArray(node.children)) return node.children.map(nodeText).join('');
    return '';
};

const isBoldElement = (node: any): boolean => node?.type === 'tag' && (node.name === 'b' || node.name === 'strong');

/** Text that is NOT inside a <b>/<strong>, so we can tell a "whole paragraph is bold" heading apart. */
const nonBoldText = (node: any): string => {
    if (!node) return '';
    if (isBoldElement(node)) return '';
    if (node.type === 'text') return node.data ?? '';
    if (Array.isArray(node.children)) return node.children.map(nonBoldText).join('');
    return '';
};

/** Only punctuation / whitespace, e.g. the ":" left dangling after a bold heading. */
const isPunctuationOnly = (text: string): boolean => /^[\s.:;,–—-]*$/.test(text);

/**
 * Rezdy authors sometimes glue a "heading" bold onto the end of a body paragraph
 * (e.g. "...Palm Cove.<br><br><strong>Inclusions</strong>:") instead of giving it its
 * own block. Since we strip <br> before parsing, that heading collapses onto the
 * sentence. Detect a trailing bold (only punctuation/whitespace after it) so we can
 * lift it onto its own line. Returns the split point, or -1 when there's no trailing
 * heading (plain paragraph or mid-sentence emphasis, which we leave inline).
 */
const trailingHeadingIndex = (children: any[]): number => {
    for (let i = children.length - 1; i >= 0; i--) {
        const node = children[i];
        if (isBoldElement(node)) return i;
        // Real content sits after a bold => it's inline emphasis, not a heading.
        if (!isPunctuationOnly(nodeText(node))) return -1;
    }
    return -1;
};

export const DisplayText = (props: { text: string }) => {
    const htmlNoBreaks = normalizeProductHtmlForDisplay(props.text);

    const options: HTMLReactParserOptions = {
        replace: (domNode) => {
            if (domNode.nodeType === 3 && !domNode.parent) {
                const newNode: any = domNode;
                return <Typography sx={textPriceSx.descriptionText}>{newNode.data}</Typography>;
            }
            if ((domNode as { type?: string }).type === 'tag') {
                // NOTE: deliberately a `type === 'tag'` check, not `domNode instanceof Element`.
                // The `Element` exported by html-react-parser doesn't share identity with the
                // class used to construct parsed nodes, so `instanceof` is always false here and
                // would let every element fall through to unstyled native HTML.
                const el = domNode as Element;
                if (el.name === 'ul' || el.name === 'ol') {
                    return <List sx={textPriceSx.list}>{domToReact(el.children as DOMNode[], options)}</List>;
                }
                if (el.name === 'li') {
                    return (
                        <ListItem sx={textPriceSx.listText}>
                            <ListItemIcon style={{ minWidth: 30 }}>•</ListItemIcon>
                            <ListItemText>{domToReact(el.children as DOMNode[], options)}</ListItemText>
                        </ListItem>
                    );
                }
                if (el.name === 'p') {
                    const kids = el.children as any[];

                    // Drop empty spacer paragraphs (Rezdy emits many <p></p> / <p><br></p>).
                    if (!nodeText(el).trim()) return <></>;

                    // Whole paragraph is bold => a heading, even when wrapped in <span> etc.
                    // (e.g. "More Information", or "<strong>Inclusions:</strong>").
                    if (!nonBoldText(el).trim()) {
                        return (
                            <Typography sx={[textPriceSx.descriptionText, textPriceSx.boldOverrideP]}>
                                {domToReact(kids as DOMNode[], options)}
                            </Typography>
                        );
                    }

                    // Body sentence with a trailing bold heading glued on (e.g. "...Palm Cove."
                    // + "Inclusions:"): render the body normally and lift the heading onto its own line.
                    const splitAt = trailingHeadingIndex(kids);
                    if (splitAt > 0 && kids.slice(0, splitAt).some((node) => nodeText(node).trim().length > 0)) {
                        return (
                            <>
                                <Typography sx={textPriceSx.descriptionText}>
                                    {domToReact(kids.slice(0, splitAt) as DOMNode[], options)}
                                </Typography>
                                <Typography sx={[textPriceSx.descriptionText, textPriceSx.boldOverrideP]}>
                                    {domToReact(kids.slice(splitAt) as DOMNode[], options)}
                                </Typography>
                            </>
                        );
                    }

                    // Plain body paragraph (mid-sentence emphasis stays inline).
                    return (
                        <Typography sx={textPriceSx.descriptionText}>
                            {domToReact(kids as DOMNode[], options)}
                        </Typography>
                    );
                }
                if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].indexOf(el.name) !== -1) {
                    const bold = el.children
                        .map((child: any) => child.name === 'b' || child.name === 'strong')
                        .includes(true);
                    return (
                        <Typography variant="h6" sx={[bold && textPriceSx.boldOverride]}>
                            {domToReact(el.children as DOMNode[], options)}
                        </Typography>
                    );
                }
                if (el.name === 'strong' && !el.parent) {
                    return (
                        <>
                            <Typography
                                sx={[textPriceSx.descriptionText, textPriceSx.boldOverrideP]}
                                style={{ fontWeight: 500 }}
                            >
                                {domToReact(el.children as DOMNode[], options)}
                            </Typography>
                            <br />
                        </>
                    );
                }
            }
        },
    };
    const stripedText = parse(htmlNoBreaks, options);

    return (
        <>
            <Box style={{ marginTop: 10 }}>{stripedText}</Box>
        </>
    );
};

interface SaleDisplayProps {
    readonly currentSales: () => Sale[];
}

export const Sales = ({ currentSales }: SaleDisplayProps) => {
    const sales = currentSales();
    const theme = useTheme();
    const smallerSize = useMediaQuery(theme.breakpoints.down('sm'));
    const xs = useMediaQuery(theme.breakpoints.only('xs'));

    return (
        <Grid container sx={textPriceSx.salesSection}>
            <Box sx={textPriceSx.salesPaper}>
                <Grid container>
                    <Grid size={{ xs: 12, sm: 4 }} sx={textPriceSx.titleSection}>
                        <NewReleasesIcon
                            style={{ color: xs ? theme.palette.primary.main : 'white', marginRight: 10 }}
                            fontSize="large"
                        />
                        <Typography
                            display="inline"
                            variant="h5"
                            style={{
                                fontSize: smallerSize ? 24 : 28,
                                fontWeight: 400,
                                color: xs ? theme.palette.primary.main : 'white',
                            }}
                        >
                            On Sale
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }} sx={textPriceSx.textSection}>
                        <Grid container>
                            {sales.map((sale, i) => (
                                <Grid container key={`saleItem${i + 1}`}>
                                    <Hidden only="xs">
                                        <Grid size={{ xs: 'auto' }}>
                                            <DateRangeIcon
                                                style={{
                                                    marginTop: 10,
                                                    marginRight: 10,
                                                }}
                                            />
                                        </Grid>
                                    </Hidden>
                                    <Grid size={'grow'}>
                                        <Typography
                                            style={{
                                                fontWeight: 500,
                                                fontSize: 18,
                                                paddingTop: 10,
                                            }}
                                        >
                                            {moment(sale.startDate).format('Do MMMM')} -{' '}
                                            {moment(sale.endDate).format('Do MMMM')}
                                        </Typography>
                                    </Grid>
                                    <Grid container style={{ marginTop: 10 }}>
                                        <Grid size={{ xs: 12, sm: 'auto' }}>
                                            {sale.fareTypes.map((fare, i) => (
                                                <Grid
                                                    container
                                                    key={`saleFare${i + 1}`}
                                                    sx={textPriceSx.saleFareSection}
                                                >
                                                    <Grid size={{ xs: 'auto' }}>
                                                        <Typography style={{ fontSize: 17 }}>{fare.label}</Typography>
                                                    </Grid>
                                                    <Grid size={'grow'} style={{ paddingLeft: 15 }}>
                                                        <Typography
                                                            style={{
                                                                paddingLeft: 30,
                                                                fontSize: 17,
                                                                textAlign: 'right',
                                                            }}
                                                        >
                                                            $ {fare.price}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Grid>
    );
};
